import { Fragment, useEffect, useMemo, useState } from 'react';
import { filter, map, tap } from 'rxjs';

import { MODELING_STATUS } from '@/constant/modelingConstant';
import { useModelingDetailQuery } from '@/hook';
import {
  DeployedCodeUpdate,
  Pageview,
  VButton,
  VFlex,
  VStack,
  VStatusBadge,
  formatDate,
  useFlag,
  useSubscriptions,
  useUnleash,
} from '@/package';
import { useMainLayout, useWebSocket } from '@/provider';
import type { WebSocketPayload } from '@/provider/webSocket/type';
import type { Modeling } from '@/type';
import { calculateTrainingTime } from '@/util/modeling';

import { GoBack } from '../GoBack';
import DataSplitModal from './DataSplitModal';
import TrainingDataModal from './TrainingDataModal';
import { useDeploymentModal } from './hook';

const ModelingDetailPageOverview = ({
  modelingId,
}: {
  modelingId: Modeling['id'];
}) => {
  const { data: modelingDetail } = useModelingDetailQuery(modelingId);
  const { setTitle } = useMainLayout();
  const { onDeploy } = useDeploymentModal();
  const [progress, setProgress] = useState<number>(0);
  const { message$ } = useWebSocket();
  const { getVariant } = useUnleash();
  const variant = getVariant('vuex.demo.modelBuilder');
  const isFeatureEnabled = variant?.feature_enabled;
  const {
    value: trainingDataModalVisible,
    setFalse: hideTrainingDataModal,
    setTrue: showTrainingDataModal,
  } = useFlag(false);
  const {
    value: dataSplitModalVisible,
    setFalse: hideDataSplitModal,
    setTrue: showDataSplitModal,
  } = useFlag(false);

  const trainingTime = useMemo(() => {
    if (!modelingDetail) return '';
    const { hours, minutes, seconds } = calculateTrainingTime(modelingDetail);

    if (hours < 0 || minutes < 0 || seconds < 0) return '-';

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [modelingDetail]);

  const columnList = [
    [
      { label: 'Model name', value: modelingDetail?.name },
      {
        label: 'Status',
        value: () => {
          if (!modelingDetail) return <div>-</div>;

          if (modelingDetail.status === 'Training') {
            return (
              <div className="w-fit">
                <VStatusBadge variant="inProgress" size="sm">
                  {progress}%
                </VStatusBadge>
              </div>
            );
          }

          return (
            <div className="w-fit">
              <VStatusBadge
                size="sm"
                variant={
                  modelingDetail.status === MODELING_STATUS.Completed
                    ? 'completed'
                    : MODELING_STATUS.Failure
                      ? 'failed'
                      : 'notStarted'
                }
              >
                {modelingDetail.status}
              </VStatusBadge>
            </div>
          );
        },
      },
    ],
    [
      { label: 'Training runs', value: modelingDetail?.trainingRun },
      {
        label: 'Type / Function',
        value: `${modelingDetail?.sensorType} / ${modelingDetail?.modelFunction}`,
      },
    ],
    [
      { label: 'Training data', value: modelingDetail?.alias, hasDetail: true },
      { label: 'Training time', value: trainingTime },
    ],
    [
      {
        label: 'Data split',
        value: modelingDetail?.splitMode,
        hasDetail: true,
      },
      {
        label: 'Created time',
        value: () => (
          <div className="text-body-md">
            {modelingDetail?.createdAt
              ? formatDate(modelingDetail.createdAt)
              : '-'}
          </div>
        ),
      },
    ],
  ];

  useSubscriptions(() => {
    if (!message$) return [];

    return [
      message$
        ?.pipe(
          filter(event => !!event),

          filter(event => event.eventType === 'MiriDetroitModelTraining'),

          map(
            event =>
              event.payload as WebSocketPayload<'MiriDetroitModelTraining'>,
          ),

          tap(event => {
            if (Number(event.modeling.id) === Number(modelingId)) {
              setProgress(event.progress);
            }
          }),
        )
        .subscribe(),
    ];
  }, [message$]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setProgress(modelingDetail?.trainingPercent ?? 0);
  }, [modelingDetail]);

  useEffect(() => {
    setTitle(() => {
      return (
        <VFlex className="gap-[10px]" items="center">
          <GoBack to={`/modeling`} />
          <div className="text-headline-sm font-normal">Modeling</div>
          <div className="h-6 w-px bg-neutral-400" />
          <div className="text-headline-sm font-semibold">
            {modelingDetail?.name ?? ''}
          </div>
        </VFlex>
      );
    });
  }, [modelingDetail?.name]);

  return (
    <VFlex className="pt-6 pb-6">
      <VStack className="w-full rounded-md px-4 py-2" gap={0.5}>
        <VFlex
          justify="between"
          items="center"
          className="w-full border-b border-b-gray-300 pb-2"
        >
          <div className="text-headline-sm font-semibold">Model details</div>
          {isFeatureEnabled && (
            <VButton
              outlined
              size="lg"
              variant="secondary"
              onClick={() => onDeploy({ modeling: modelingDetail })}
            >
              <DeployedCodeUpdate />
              Deployment
            </VButton>
          )}
        </VFlex>

        <VFlex className="w-full" gap={0.25}>
          {columnList.map((row, index, arr) => (
            <Fragment key={index}>
              <VStack className="divide-y-px flex-1 divide-gray-300 border-b border-b-gray-300">
                {row.map((item, index) => (
                  <DetailItem
                    key={index}
                    {...item}
                    onDetailClick={() => {
                      if (item.label === 'Training data') {
                        showTrainingDataModal();
                      } else if (item.label === 'Data split') {
                        showDataSplitModal();
                      }
                    }}
                  />
                ))}
              </VStack>

              {index !== arr.length - 1 && (
                <div className="h-full w-px bg-gray-300" />
              )}
            </Fragment>
          ))}
        </VFlex>
      </VStack>

      {modelingDetail && (
        <>
          <TrainingDataModal
            modelingDetail={modelingDetail}
            visible={trainingDataModalVisible}
            onUpdateVisible={v =>
              v ? showTrainingDataModal() : hideTrainingDataModal()
            }
          />

          <DataSplitModal
            modelingDetail={modelingDetail}
            visible={dataSplitModalVisible}
            onUpdateVisible={v =>
              v ? showDataSplitModal() : hideDataSplitModal()
            }
          />
        </>
      )}
    </VFlex>
  );
};

const DetailItem = ({
  label,
  value,
  hasDetail,
  onDetailClick,
}: {
  label: string;
  value: string | number | undefined | (() => React.ReactNode);
  hasDetail?: boolean;
  onDetailClick?(): void;
}) => (
  <VFlex className="h-8 px-2 py-1 text-body-md" gap={1} items="center">
    <div className="w-[108px] text-gray-500">{label}</div>
    <VFlex className="flex-1" justify="between" items="center">
      <div>{typeof value === 'function' ? value() : value}</div>
      {hasDetail && (
        <Pageview
          role="button"
          className="size-4 text-primary-500"
          onClick={onDetailClick}
        />
      )}
    </VFlex>
  </VFlex>
);

export default ModelingDetailPageOverview;
